import { Empty, Table, Typography } from 'antd';
import { useMemo } from 'react';

import { formatCurrency, formatKg } from '../../../utils/format';
import {
  ProductCell,
  TwoLineText,
  VolumeText,
  indexColumn,
  num,
  sumBy,
  withStatusBackground,
} from './productTableParts';
import './style.less';

const { Text } = Typography;

/**
 * Татан авалтын барааны хүснэгт.
 * Хүргэлтийн дэлгэрэнгүй дээрх барааны хүснэгттэй ижил төрхтэй.
 *
 * `status` — захиалгын төлөв. Мөрөнд өөрийн төлөв (`orderStatus`) байвал тэр
 * давуу, үгүй бол захиалгын төлвөөр бүх мөрийг будна.
 */
const ProcurementProductTable = ({ products = [], status }) => {
  /** Эрлиан агуулахын бараанд SKU-ийн оронд баркод харуулна */
  const isErlian = products?.[0]?.requesterCode === 'EHW';

  const columns = useMemo(() => {
    /** Мөрийн төлөв: мөрийнх нь эхэнд, байхгүй бол захиалгынх */
    const rowStatus = (record) => record?.orderStatus ?? status;

    return withStatusBackground(
      [
        indexColumn,
        {
          key: 'product',
          title: 'Бараа',
          render: (_, record) => (
            <ProductCell
              danger={rowStatus(record) === 'canceled'}
              image={record?.productImage ?? record?.imageCover}
              title={record?.productTitle ?? record?.title}
              code={isErlian ? record?.barcodes?.[0] : (record?.productSKU ?? record?.productSku)}
            />
          ),
        },
        {
          key: 'total',
          title: 'Дүн / тоо',
          align: 'center',
          width: 140,
          render: (_, record) => (
            <TwoLineText
              align="end"
              primary={record?.totalPrice ? formatCurrency(record.totalPrice) : '-'}
              secondary={`${num(record?.totalUnit ?? record?.stock)} ширхэг`}
            />
          ),
        },
        {
          key: 'weight',
          title: 'Жин / эзэлхүүн',
          align: 'center',
          width: 140,
          render: (_, record) => (
            <TwoLineText
              align="end"
              primary={`${formatKg(record?.totalWeight)} кг`}
              secondary={<VolumeText value={record?.totalVolume} />}
            />
          ),
        },
      ],
      rowStatus
    );
  }, [isErlian, status]);

  /** Нийт дүн, тоо, жин, эзэлхүүн */
  const renderSummary = (rows = []) => (
    <Table.Summary.Row>
      <Table.Summary.Cell colSpan={2} />
      <Table.Summary.Cell align="center">
        <TwoLineText
          align="end"
          primary={<Text strong>{formatCurrency(sumBy(rows, (row) => row.totalPrice))}</Text>}
          secondary={`${sumBy(rows, (row) => row.totalUnit)} ширхэг`}
        />
      </Table.Summary.Cell>
      <Table.Summary.Cell align="center">
        <TwoLineText
          align="end"
          primary={<Text strong>{`${formatKg(sumBy(rows, (row) => row.totalWeight))} кг`}</Text>}
          secondary={<VolumeText value={sumBy(rows, (row) => row.totalVolume)} />}
        />
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  if (products.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Бараа байхгүй байна."
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <Table
      className="procurement__product__table"
      size="small"
      columns={columns}
      dataSource={products}
      pagination={false}
      rowKey={(record, index) => record?._id ?? index}
      summary={renderSummary}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default ProcurementProductTable;
